import { useState } from 'react';
import { useAccount, usePublicClient, useReadContracts } from 'wagmi';
import { toast } from 'react-toastify';
import { Web3Button } from './Web3Button';
import { rewardTokenAddress, rewardTokenSymbol } from './Home';
import { erc20Abi, formatUnits, isAddress, parseUnits } from 'viem';
import { RewardTokenAbi, RewardTokenAbi2 } from './abi/abis';

const dividendTokenAddress = '0x2fa878Ab3F87CC1C9737Fc071108F904c0B0C95d'; // INC
const dividendTokenSymbol = 'INC';
const dividendTokenDecimals = 18;

export default function AddLiquidity() {
  const { address } = useAccount();
  const client = usePublicClient();

  const [devMintAmount, setDevMintAmount] = useState<string>('10000')
  const [devTimeWarp, setDevTimeWarp] = useState<string>('10000')
  const [testAddress, setTestAddress] = useState<string>(address ?? '0x')
  const [testResult, setTestResult] = useState<string>('')

  const { data } = useReadContracts({
    contracts: [
      {
        address: rewardTokenAddress,
        abi: RewardTokenAbi2,
        functionName: 'firstPeriod',
      },
      {
        address: rewardTokenAddress,
        abi: RewardTokenAbi,
        functionName: 'currentPeriod',
      },
      {
        address: rewardTokenAddress,
        abi: RewardTokenAbi,
        functionName: 'currentPeriodRealTime',
      },
      {
        address: dividendTokenAddress,
        abi: erc20Abi,
        args: [rewardTokenAddress ?? '0x'],
        functionName: 'balanceOf',
      },
      {
        address: rewardTokenAddress,
        abi: erc20Abi,
        args: [rewardTokenAddress ?? '0x'],
        functionName: 'balanceOf',
      },
      {
        address: rewardTokenAddress,
        abi: RewardTokenAbi,
        functionName: 'mainV2Pair',
      },
      {
        address: rewardTokenAddress,
        abi: RewardTokenAbi,
        functionName: 'minSwapThreshold',
      },
    ],
    allowFailure: false,
    query: {
      refetchInterval: 3000,
    }
  });

  const [
    data_firstPeriod,
    data_currentPeriod,
    data_currentPeriodRealTime,
    data_dividendTokenBalance,
    data_rewardTokenBalance,
    data_mainV2Pair,
    data_minSwapThreshold
  ] = data ?? [undefined, undefined, undefined, undefined, undefined, undefined]

  const { data: devData } = useReadContracts({
    contracts: [
      {
        address: rewardTokenAddress,
        abi: RewardTokenAbi,
        functionName: 'timeWarpSeconds',
      },
    ],
    allowFailure: false,
    query: {
      refetchInterval: 3000,
    }
  });
  const [devData_timeWarpSeconds] = devData ?? [undefined]

  return (
    <div className="border-2 border-green-900 bg-gradient-to-b from-green-950 p-3 rounded-xl">
      <div className="grid grid-cols-[auto_auto] gap-2 items-center">
        <div className="col-span-2 flex flex-row">
          <div className="label text-bold text-xl grow">{rewardTokenSymbol} Information and Actions</div>
        </div>

        <div className="label">First period (deployment):</div>
        <input className="input w-[25rem]" type="text" value={`${data_firstPeriod}`} disabled />

        <div className="label">Current period (real time):</div>
        <input className="input w-[25rem]" type="text" value={`${data_currentPeriodRealTime}`} disabled />

        <div className="label">Current period (contract time):</div>
        <input className="input w-[25rem]" type="text" value={`${data_currentPeriod}`} disabled />

        <div className="label">Accrued {rewardTokenSymbol}:</div>
        <input className="input w-[25rem]" type="text" value={`${formatUnits(data_rewardTokenBalance ?? -1n, dividendTokenDecimals)}`} disabled />

        <div className="label">Accrued {dividendTokenSymbol}:</div>
        <input className="input w-[25rem]" type="text" value={`${formatUnits(data_dividendTokenBalance ?? -1n, dividendTokenDecimals)}`} disabled />

        <div className="col-span-2 my-2 border-t border-green-700"></div>

        <div className="label">Main V2 pair:</div>
        <input className="input w-[25rem]" type="text" value={data_mainV2Pair ?? '?'} disabled />

        <div className="label">Auto-swap threshold if above:</div>
        <input className="input w-[25rem]" type="text" value={`${Number(data_minSwapThreshold) / 1e18}`} disabled />

        <div className="col-span-2 my-2 border-t border-green-700"></div>

        <div className="label">Dev: Time warp (second):</div>
        <input className="input w-[25rem]" type="text" value={`${devData_timeWarpSeconds}`} disabled />
        <div className="label">Dev: Time warp offset (seconds):</div>
        <div className="flex flex-row gap-2">
          <input className="input w-[5rem]" type="text" value={devTimeWarp} onChange={(e) => setDevTimeWarp(e.target.value)} />
          <button className='btn' onClick={() => setDevTimeWarp(`3600`)}>1h</button>
          <button className='btn' onClick={() => setDevTimeWarp(`${6*3600}`)}>6h</button>
          <button className='btn' onClick={() => setDevTimeWarp(`${12*3600}`)}>12h</button>
          <button className='btn' onClick={() => setDevTimeWarp(`86400`)}>1d</button>
          <button className='btn' onClick={() => setDevTimeWarp(`${86400 * 7}`)}>7d</button>
        </div>

        <div className="col-span-2 flex flex-row justify-end">
          <Web3Button
            className="w-48"
            contractAddress={rewardTokenAddress}
            contractAbi={RewardTokenAbi}
            action={async ({ estimateAndSend }) => {
              const seconds = parseInt(devTimeWarp);
              const args: any[] = [seconds];
              console.log("devOnlyWarpTime", args);
              const promise = estimateAndSend("devOnlyWarpTime", args);
              toast.promise(promise, { pending: `Dev: Advancing time (devOnlyWarpTime) by ${seconds} seconds...` });
              return await promise;
            }}
            onError={async (err: any) => {
              console.error(err);
              toast.error(`Failed to execute devOnlyWarpTime: ${err.shortMessage ?? err}`);
            }}
            onSuccess={async () => {
              toast.success('Executed devOnlyWarpTime succeed');
            }}
            disabled={!(parseInt(devTimeWarp) !== undefined)}
          >
            Dev: Time Warp
          </Web3Button>
        </div>

        <div className="col-span-2 my-2 border-t border-green-700"></div>

        <div className="label">Dev: Mint amount:</div>
        <input className="input w-[25rem]" type="text" value={devMintAmount} onChange={(e) => setDevMintAmount(e.target.value)} />

        <div className="col-span-2 flex flex-row justify-end">
          <Web3Button
            className="w-48"
            contractAddress={rewardTokenAddress}
            contractAbi={RewardTokenAbi}
            action={async ({ readContractCustom, estimateAndSend, estimateAndSendCustom }) => {
              const args: any[] = [address, parseUnits(devMintAmount, 18)];
              console.log("devOnlyMint", args);
              const promise = estimateAndSend("devOnlyMint", args);
              toast.promise(promise, { pending: 'devOnlyMint...' });
              return await promise;
            }}
            onError={async (err: any) => {
              console.error(err);
              toast.error(`Failed to execute devOnlyMint: ${err.shortMessage ?? err}`);
            }}
            onSuccess={async () => {
              toast.success('Executed devOnlyMint succeed');
            }}
            disabled={!(parseFloat(devMintAmount) !== undefined)}
          >
            Dev: Mint
          </Web3Button>
        </div>

        <div className="col-span-2 my-2 border-t border-green-700"></div>

        <div className="label">Test address:</div>
        <div className="flex flex-row items-center gap-2">
          <input className={"input w-[25rem]"} type="text" value={testAddress} onChange={(e) => setTestAddress(e.target.value)} />
          {!(isAddress(testAddress)) && <div className="text-red-700 text-2xl">⚠</div>}
        </div>
        <div className="label">Test result:</div>
        <input className="input w-[25rem]" type="text" value={testResult} disabled />

        <div className="col-span-2 flex flex-row justify-end">
          <button className='btn' onClick={async () => {
            setTestResult('')
            if(!client) return;
            const res = await client.readContract({
              address: rewardTokenAddress,
              abi: RewardTokenAbi,
              functionName: 'claimableDividends',
              args: [testAddress]
            } as const);
            setTestResult(formatUnits(res, 18))
          }}>Get Claimable Dividends</button>
        </div>
        <div className="col-span-2 my-2 border-t border-green-700"></div>

        <div className="col-span-2 flex flex-row justify-center gap-2">
          <Web3Button
            className="w-48"
            contractAddress={rewardTokenAddress}
            contractAbi={RewardTokenAbi}
            action={async ({ estimateAndSend }) => {
              const args: any[] = [];
              console.log("handleFeeDistribution", args);
              const promise = estimateAndSend("handleFeeDistribution", args);
              toast.promise(promise, { pending: 'handleFeeDistribution...' });
              return await promise;
            }}
            onError={async (err: any) => {
              console.error(err);
              toast.error(`Failed to execute handleFeeDistribution: ${err.shortMessage ?? err}`);
            }}
            onSuccess={async () => {
              toast.success('Executed handleFeeDistribution succeed');
            }}
            disabled={!(parseInt(devTimeWarp) !== undefined)}
          >
            Handle Fee Distribution
          </Web3Button>

          <Web3Button
            className="w-48"
            contractAddress={rewardTokenAddress}
            contractAbi={RewardTokenAbi}
            action={async ({ estimateAndSend }) => {
              const args: any[] = [];
              console.log("claimDividends", args);
              const promise = estimateAndSend("claimDividends", args);
              toast.promise(promise, { pending: 'claimDividends...' });
              return await promise;
            }}
            onError={async (err: any) => {
              console.error(err);
              toast.error(`Failed to execute claimDividends: ${err.shortMessage ?? err}`);
            }}
            onSuccess={async () => {
              toast.success('Executed claimDividends succeed');
            }}
            disabled={!(parseInt(devTimeWarp) !== undefined)}
          >
            Claim Dividends
          </Web3Button>

          <Web3Button
            className="w-48"
            contractAddress={rewardTokenAddress}
            contractAbi={RewardTokenAbi}
            action={async ({ estimateAndSend }) => {
              const args: any[] = [];
              console.log("rescueIncDisable", args);
              const promise = estimateAndSend("rescueIncDisable", args);
              toast.promise(promise, { pending: 'rescueIncDisable...' });
              return await promise;
            }}
            onError={async (err: any) => {
              console.error(err);
              toast.error(`Failed to execute rescueIncDisable: ${err.shortMessage ?? err}`);
            }}
            onSuccess={async () => {
              toast.success('Executed rescueIncDisable succeed');
            }}
            disabled={!(parseInt(devTimeWarp) !== undefined)}
          >
            Disable INC Rescue
          </Web3Button>
        </div>

      </div>
    </div>
  );
}
